import React, { useEffect } from "react";
import {
  Typography,
  Box,
  MenuItem,
  Select,
  Tooltip,
  TextField
} from "@material-ui/core";
import { useTranslation } from "@/langs/useTranslation";
import styles from "./index.module.less";
import ConfigSwitch from "./common/ConfigSwitch";
import Countries from "@/assets/ISO3166";
import countryTimezone from "country-timezone";
import { HelpOutline } from "@material-ui/icons";
import { TimePicker } from "@material-ui/pickers";
import { makeStyles } from "@material-ui/core/styles";
import dayjs from "dayjs";
import { getCompanySettingInfo } from "@/apis/apiCollection";
import { generateIntlHash } from "@/helpers/utils";
import HasPermission from "@/components/commons/HasPermission";

const SystemConfig = ({ values, onChange, readOnly }) => {
  const { t } = useTranslation();
  const tt = t;
  const classes = useStyles();

  useEffect(() => {
    if (!values.update?.update_start && !values.update?.update_end) {
      getCompanySettingInfo().then(data => {
        onChange({
          target: {
            name: "systemSetting.update.update_start",
            value: dayjs(data.updateStart, "HH:mm:ss").format("HH:mm")
          }
        });
        onChange({
          target: {
            name: "systemSetting.update.update_end",
            value: dayjs(data.updateEnd, "HH:mm:ss").format("HH:mm")
          }
        });
      });
    }
  }, []);

  return (
    <Box flex={1}>
      <Typography variant="h5" style={{ marginBottom: 32 }}>
        {t({ defaultMessage: "系统设置" })}
      </Typography>
      <ConfigSwitch
        title={t({ defaultMessage: "是否启用" })}
        name="systemSetting.open"
        onChange={onChange}
        marginBottom={3}
        value={values.open}
      />
      {values.open && (
        <>
          <div className={styles.card}>
            <Typography variant="h5">
              {t({ defaultMessage: "时间" })}
            </Typography>
            <ConfigSwitch
              marginTop={3}
              title={t({ defaultMessage: "手动设置时区" })}
              name="systemSetting.time.manual"
              onChange={onChange}
              value={values.time.manual}
              tip={t({ defaultMessage: "此配置只针对特定设备" })}
            />
            {values.time.manual && (
              <>
                <Typography
                  variant="h6"
                  style={{ marginTop: 24, marginBottom: 16 }}
                >
                  {t({ defaultMessage: "区域" })}
                </Typography>
                <Select
                  name="systemSetting.time.region"
                  variant="outlined"
                  value={values.time.region}
                  fullWidth
                  className={styles.select}
                  // onChange={onChange}
                  onChange={e => {
                    onChange(e);
                    onChange({
                      target: {
                        name: "systemSetting.time.timezone",
                        value: countryTimezone.getTimezonesWithCountryCode(
                          e.target.value
                        )[0]
                      }
                    });
                  }}
                  disabled={readOnly}
                >
                  {Countries.map(country => {
                    return (
                      <MenuItem value={country.alpha2} key={country.alpha3}>
                        {tt({ id: generateIntlHash(country.translation) })}
                      </MenuItem>
                    );
                  })}
                </Select>
                <Typography
                  variant="h6"
                  style={{ marginTop: 24, marginBottom: 16 }}
                >
                  {t({ defaultMessage: "时区" })}
                </Typography>
                <Select
                  name="systemSetting.time.timezone"
                  variant="outlined"
                  value={values.time.timezone}
                  fullWidth
                  className={styles.select}
                  onChange={onChange}
                  disabled={readOnly}
                >
                  {countryTimezone
                    .getTimezonesWithCountryCode(values.time.region)
                    .map(tz => {
                      return (
                        <MenuItem value={tz} key={tz}>
                          {tt({ id: generateIntlHash(tz) })}
                        </MenuItem>
                      );
                    })}
                </Select>
              </>
            )}
            <ConfigSwitch
              marginTop={2}
              title={t({ defaultMessage: "启用24小时格式" })}
              name="systemSetting.time.24hour"
              onChange={onChange}
              value={values.time["24hour"]}
            />
          </div>
          <HasPermission permission="profile_template_android_system_settings_update">
            <div className={styles.card} style={{ marginTop: 24 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h5">
                  {t({ defaultMessage: "更新" })}
                </Typography>
                <Tooltip title={t({ defaultMessage: "此配置只针对特定设备" })}>
                  <HelpOutline />
                </Tooltip>
              </Box>
              <ConfigSwitch
                marginTop={2}
                title={t({ defaultMessage: "启用系统更新" })}
                name="systemSetting.update.system_update"
                onChange={onChange}
                value={values.update.system_update}
              />
              {values.update.system_update && (
                <ConfigSwitch
                  marginTop={2}
                  title={t({ defaultMessage: "启用静默更新" })}
                  name="systemSetting.update.silent_update"
                  onChange={onChange}
                  value={values.update.silent_update}
                />
              )}
              {values.update.silent_update && (
                <div className={classes.rangeTimePicker}>
                  <Typography variant="h6" style={{ marginRight: "auto" }}>
                    {t({ defaultMessage: "执行静默更新时间" })}
                  </Typography>
                  <TimePicker
                    onChange={e => {
                      onChange({
                        target: {
                          name: "systemSetting.update.update_start",
                          value: dayjs(e).format("HH:mm")
                        }
                      });
                    }}
                    renderInput={props => (
                      <TextField
                        {...props}
                        helperText=""
                        inputProps={{
                          ...props.inputProps,
                          value: values.update.update_start,
                          placeholder: t({ defaultMessage: "开始时间" })
                        }}
                      />
                    )}
                  />
                  <span className={classes.rangeDelimiter}>-</span>
                  <TimePicker
                    onChange={e => {
                      onChange({
                        target: {
                          name: "systemSetting.update.update_end",
                          value: dayjs(e).format("HH:mm")
                        }
                      });
                    }}
                    renderInput={props => (
                      <TextField
                        {...props}
                        helperText=""
                        inputProps={{
                          ...props.inputProps,
                          value: values.update.update_end,
                          placeholder: t({ defaultMessage: "结束时间" })
                        }}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </HasPermission>

        </>
      )}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  rangeTimePicker: {
    display: "flex",
    marginBottom: theme.spacing(2.5),
    marginTop: 16
  },
  rangeDelimiter: {
    margin: `0 ${theme.spacing(2)}px`,
    alignSelf: "center",
    color: theme.palette.text.secondary
  }
}));

export const initValues = {
  open: false,
  time: {
    manual: true,
    region: "CN",
    timezone: "Asia/Shanghai",
    "24hour": true
  },
  update: {
    system_update: false,
    silent_update: false,
    update_start: 0,
    update_end: 0
  }
};
export default SystemConfig;
